import React from 'react';

/**
 * A page in the stack, consisting of a unique ID and a React node provider.
 */
export interface StackPage {
  id: string;
  node: React.ReactNode;
}

export type StackApi = {
  /**
   * Pushes a new page onto the stack. If a page with the same ID already exists, it will be replaced.
   *
   * @param {StackPage} page Page to push onto the stack.
   */
  push: (page: StackPage) => void;

  /**
   * Pushes a new page onto the stack. After the animation completes, only the previous root page and the new page will
   * be on the stack.
   *
   * @param {StackPage} page Page to push onto the stack.
   */
  rootPush: (page: StackPage) => void;

  /**
   * Replaces the stack page with the same ID with the provided page. If no page with the same ID exists, nothing will
   * happen.
   *
   * @param {StackPage} page Page to replace.
   */
  replace: (page: StackPage) => void;

  /**
   * Pops the top page from the stack. If there is only one page, it will not be removed.
   */
  pop: () => void;
};

/**
 * Enumeration of possible visible stack item transitions.
 */
export enum StackPageDisplayState {
  VISIBLE               = 'VISIBLE',
  HIDDEN                = 'HIDDEN',
  ANIMATE_IN_FROM_RIGHT = 'ANIMATE_IN_FROM_RIGHT',
  ANIMATE_IN_FROM_LEFT  = 'ANIMATE_IN_FROM_LEFT',
  ANIMATE_OUT_TO_RIGHT  = 'ANIMATE_OUT_TO_RIGHT',
  ANIMATE_OUT_TO_LEFT   = 'ANIMATE_OUT_TO_LEFT'
}
